import { useMemo } from 'react';
import styled from 'styled-components';
import type {
  AccordionBodyRichText,
  AccordionItemHeading,
  Image,
  ImageAccordion as ImageAccordionProps,
} from '@amplience/content-types/typings/c-image-accordion';
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from '@storefront/lib-global/baseComponents/accordion';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { Plus } from '@storefront/lib-global/icons';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import {
  TypographyStyles,
  colours,
  maxWidthPartial,
  media,
  radixAccordionSlideDown,
  radixAccordionSlideUp,
  spacing,
} from '@storefront/lib-global/stylings';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';

const S = {
  Root: styled(AccordionRoot)`
    display: grid;
    gap: ${spacing.S};
    grid-template-columns: 1fr;

    svg {
      height: 11.12px;
      width: 11.12px;
    }

    @media ${media.greaterThan('lg')} {
      column-gap: ${spacing.M};
      grid-template-columns: repeat(4, minMax(200px, 301px));
      row-gap: ${spacing.L};

      .item {
        grid-column: span 2;
      }

      .item:nth-last-child(1):nth-child(odd) {
        grid-column: 2 / span 2;
      }
    }
  `,
  Item: styled(AccordionItem)`
    overflow: hidden;
    overflow-wrap: break-word;
    width: 100%;
  `,
  Header: styled(AccordionHeader)`
    margin: 0;
    overflow: hidden;
  `,
  Trigger: styled(AccordionTrigger)`
    align-items: center;
    background-color: ${colours.LIGHT_GREY_2};
    border: none;
    display: grid;
    gap: ${spacing.S};
    grid-template-columns: 60px auto;
    padding: 20px;

    width: 100%;
    span {
      ${TypographyStyles.Headings.H3}
      color: ${colours.BLACK};
      text-align: left;
    }
    &[data-state='open'] {
      @media screen and (prefers-reduced-motion: reduce) {
        .rotate {
          transform: rotate(45deg);
          transition: none;
        }
      }
      .rotate {
        transform: rotate(45deg);
        transition: transform 0.3s ease-in-out;
      }
    }

    &[data-state='closed'] {
      @media screen and (prefers-reduced-motion: reduce) {
        .rotate {
          transform: rotate(0);
          transition: none;
        }
      }
      .rotate {
        transform: rotate(0);
        transition: transform 0.3s ease-in-out;
      }
    }
    @media ${media.greaterThan('lg')} {
      display: flex;
      gap: ${spacing.M};
      padding: ${spacing.M};
    }
  `,
  Content: styled(AccordionContent)`
    background-color: ${colours.LIGHT_GREY_2};
    overflow: hidden;
    padding: 0 ${spacing.M} ${spacing.M};
    width: 100%;
    * {
      color: ${colours.BLACK};
      margin: 0;
    }

    @media screen and (prefers-reduced-motion: reduce) {
      &[data-state='open'] {
        animation: none;
      }
    }

    &[data-state='open'] {
      animation: ${radixAccordionSlideDown} 0.2s ease-out forwards;
    }

    @media screen and (prefers-reduced-motion: reduce) {
      &[data-state='closed'] {
        animation: none;
      }
    }

    &[data-state='closed'] {
      animation: ${radixAccordionSlideUp} 0.2s ease-in forwards;
    }
  `,
  Image: styled(ImagePartial)`
    border-radius: 50%;
  `,
  IconWrapper: styled.div`
    align-items: center;
    background-color: ${colours.WHITE};
    border: 1.7px solid ${colours.BLACK};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    min-height: 30px;
    min-width: 30px;
    @media ${media.greaterThan('lg')} {
      min-height: 37px;
      min-width: 37px;
    }
  `,
  HeaderTextWrapper: styled.div`
    align-items: center;
    display: grid;
    gap: 20px;
    grid-template-columns: auto 30px;
    width: 100%;
    @media ${media.greaterThan('lg')} {
      display: flex;
      justify-content: space-between;
    }
  `,
  ImageWrapper: styled.div`
    align-items: center;
    display: flex;
    height: 60px;
    width: 60px;
    @media ${media.greaterThan('lg')} {
      min-height: 100px;
      min-width: 100px;
    }
  `,
  Container: styled.div`
    ${maxWidthPartial({ withPaddingXL: true })}
    margin: 0 auto;
    max-width: 1440px;
  `,
};

type AccordionProps = {
  heading: AccordionItemHeading;
  image: Image;
  body?: AccordionBodyRichText;
};

type AccordionItemProps = AccordionProps & {
  index: number;
};

function ImageAccordion({ heading, image, body, index }: AccordionItemProps) {
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const richText = (body?.[0] as AccordionBodyRichText)?.richText;

  return (
    <S.Item value={`accordion-item-${index}`} className="item">
      <S.Header>
        <S.Trigger>
          <S.ImageWrapper>
            <S.Image
              altText={image.altText}
              height={isDesktop ? 100 : 60}
              src={image.image?.name ?? ''}
              assetType={image.assetType}
              width={isDesktop ? 100 : 60}
              defaultHost={image.image?.defaultHost}
              useOriginalSize={image.useOriginalSize}
            />
          </S.ImageWrapper>
          <S.HeaderTextWrapper>
            <span>{getLocalizedValue(heading)}</span>
            <S.IconWrapper className="rotate">
              <Plus viewBox="0 0 18 20" />
            </S.IconWrapper>
          </S.HeaderTextWrapper>
        </S.Trigger>
      </S.Header>
      <S.Content>
        <RichTextPartial fontSize={TypographyStyles.Body.Small.Regular} text={getLocalizedValue(richText)} />
      </S.Content>
    </S.Item>
  );
}

export function ImageAccordions({ imageAccordions }: ImageAccordionProps) {
  const accordions = useMemo(
    () => imageAccordions?.filter((imageAccordion) => imageAccordion.heading && imageAccordion.image),
    [imageAccordions],
  );
  if (!imageAccordions) return null;

  return (
    <S.Container>
      <S.Root type="multiple">
        {accordions?.map((imageAccordion, index) => (
          <ImageAccordion
            {...(imageAccordion as unknown as AccordionProps)}
            index={index}
            key={`accordion-item-${index}`}
          />
        ))}
      </S.Root>
    </S.Container>
  );
}

export default ImageAccordions;
